import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Autocomplete
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { APIProvider } from '@vis.gl/react-google-maps';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import PlaceAutocomplete from '../common/PlaceAutocomplete';
import { geocodePreciseAddress, hasCoordinates } from '../../utils/addressGeocoding';

import {
  fetchRecurringRides,
  updateRecurringOrder
} from '../../redux/slices/ridesSlice';
import { fetchMedicalTransportOrders } from '../../redux/slices/medicalTransportOrdersSlice';
import { formatDate } from '../medicalTransportOrders/medicalTransportOrderOptions';
import { buildMedicalTransportSnapshot } from '../medicalTransportOrders/medicalTransportSnapshot';
import { db } from '../../firebase/config';
import { VEHICLE_TYPE_OPTIONS } from '../../config/vehicleTypes';
import {
  WEEKDAY_OPTIONS as WEEKDAYS,
  OPEN_RIDE_STATUSES,
  toRideDate,
  getFutureRides,
  deriveWeekdays,
  getFlagMismatchRides
} from '../../utils/recurringOrderInfo';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';

const RecurringOrderEditForm = () => {
  const { recurringOrderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { recurringOrders, isLoading, error } = useSelector(state => state.rides);
  const {
    orders: medicalTransportOrders,
    isLoading: medicalTransportOrdersLoading
  } = useSelector(state => state.medicalTransportOrders);

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    start: '',
    destination: '',
    start_location: { latitude: null, longitude: null },
    destination_location: { latitude: null, longitude: null },
    vehicle_type: 1,
    recurring_weekdays: [],
    recurring_time: new Date().setHours(8, 0, 0, 0),
    current_end_date: null,
    new_end_date: null,
    is_rechnungsfahrt: false,
    invoice_customer_id: '',
    is_krankenfahrt: false,
    medical_transport_order_id: ''
  });

  const [currentOrder, setCurrentOrder] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [preview, setPreview] = useState({ updateCount: 0, deleteCount: 0, newCount: 0 });
  const [invoiceCustomers, setInvoiceCustomers] = useState([]);
  // Abweichende Fahrttyp-Flags innerhalb der Serie (Altdaten bzw. einzeln
  // umgestellte Fahrten). Wird nur als Hinweis angezeigt — beim Speichern
  // gewinnt die Auswahl im Formular für alle zukünftigen Fahrten.
  const [mixedFlags, setMixedFlags] = useState(null);
  // Verordnung/Snapshot der Referenzfahrt. Dient als Fallback, falls die
  // Verordnung selbst nicht in der Liste steht (z.B. Sichtbarkeit des
  // Operators) — sonst würde ein Speichern den Snapshot leeren.
  const [initialMedical, setInitialMedical] = useState({ orderId: '', snapshot: null });

  useEffect(() => {
    // Lade Daueraufträge wenn noch nicht geladen
    if (recurringOrders.length === 0) {
      dispatch(fetchRecurringRides());
    }
  }, [dispatch, recurringOrders.length]);

  // Verordnungen für die Krankenfahrt-Zuordnung der Serie.
  useEffect(() => {
    dispatch(fetchMedicalTransportOrders());
  }, [dispatch]);

  // Aktive Rechnungskunden laden (für die Rechnungsfahrt-Zuordnung).
  useEffect(() => {
    let active = true;
    getDocs(query(collection(db, 'invoiceCustomers'), orderBy('displayName')))
      .then((snap) => {
        if (!active) return;
        setInvoiceCustomers(
          snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => c.active !== false)
        );
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const order = recurringOrders.find(o => o.id === recurringOrderId);
    if (!order) return;

    setCurrentOrder(order);

    // Nur noch nicht gestartete/abgeschlossene Fahrten sind für die Bearbeitung
    // relevant. Pausierte Termine bleiben bewusst außen vor: das Speichern
    // fasst sie nicht an, also darf das Formular sie auch nicht beschreiben.
    const futureRides = getFutureRides(order, { statuses: OPEN_RIDE_STATUSES });

    // Referenzfahrt: erste zukünftige Fahrt. Fallback auf die allgemein erste Fahrt,
    // damit das Formular auch bei bereits abgelaufenen Serien noch befüllt wird.
    const referenceRide = futureRides[0] || order.rides?.[0];
    if (!referenceRide) return;

    const weekdays = deriveWeekdays(futureRides);
    const weekdaysFallback = weekdays.length > 0
      ? weekdays
      : deriveWeekdays(order.rides || []);

    const lastFutureRide = futureRides[futureRides.length - 1];
    const lastRideDate = lastFutureRide ? toRideDate(lastFutureRide.scheduledTime) : new Date();

    setFormData({
      customer_name: referenceRide.customerName || '',
      customer_phone: referenceRide.customerPhone || '',
      start: referenceRide.start || '',
      destination: referenceRide.destination || '',
      start_location: {
        latitude: referenceRide.startLatitude || null,
        longitude: referenceRide.startLongitude || null,
      },
      destination_location: {
        latitude: referenceRide.destinationLatitude || null,
        longitude: referenceRide.destinationLongitude || null,
      },
      vehicle_type: referenceRide.vehicleType || 1,
      recurring_weekdays: weekdaysFallback,
      recurring_time: toRideDate(referenceRide.scheduledTime)?.getTime() ?? new Date().setHours(8, 0, 0, 0),
      current_end_date: lastRideDate,
      new_end_date: lastRideDate,
      // Bei Krankenfahrt-Serien immer false — repariert nebenbei Altdaten,
      // die fälschlich beide Flags tragen (Exklusivität, s. RideForm).
      is_rechnungsfahrt: referenceRide.isKrankenfahrt ? false : (referenceRide.isRechnungsfahrt || false),
      invoice_customer_id: referenceRide.isKrankenfahrt ? '' : (referenceRide.invoiceCustomerId || ''),
      is_krankenfahrt: !!referenceRide.isKrankenfahrt,
      medical_transport_order_id: referenceRide.isKrankenfahrt
        ? (referenceRide.medicalTransportOrderId || '')
        : '',
    });

    setInitialMedical({
      orderId: referenceRide.isKrankenfahrt ? (referenceRide.medicalTransportOrderId || '') : '',
      snapshot: referenceRide.isKrankenfahrt ? (referenceRide.medicalTransportSnapshot || null) : null,
    });

    // Uneinheitliche Serie erkennen: Fahrten, deren Fahrttyp von der
    // Referenzfahrt abweicht oder die beide Flags tragen (Altdaten vor der
    // Exklusivitäts-Regel). Beim Speichern werden sie vereinheitlicht.
    const relevantRides = futureRides.length > 0 ? futureRides : (order.rides || []);
    const deviating = getFlagMismatchRides(relevantRides, referenceRide);
    setMixedFlags(deviating.length > 0
      ? { count: deviating.length, total: relevantRides.length }
      : null);
  }, [recurringOrders, recurringOrderId]);

  const selectedMedicalOrder = useMemo(
    () => medicalTransportOrders.find(o => o.id === formData.medical_transport_order_id) || null,
    [medicalTransportOrders, formData.medical_transport_order_id]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleWeekdayChange = (weekday) => {
    setFormData(prev => ({
      ...prev,
      recurring_weekdays: prev.recurring_weekdays.includes(weekday)
        ? prev.recurring_weekdays.filter(w => w !== weekday)
        : [...prev.recurring_weekdays, weekday]
    }));
  };

  const handleTimeChange = (e) => {
    const timeString = e.target.value; // "HH:MM"
    const [hours, minutes] = timeString.split(':').map(Number);
    const timeDate = new Date();
    timeDate.setHours(hours, minutes, 0, 0);
    
    setFormData(prev => ({
      ...prev,
      recurring_time: timeDate.getTime()
    }));
  };

  const buildUpdateData = () => {
    const firstRide = currentOrder?.rides?.[0];

    const startCoords = formData.start_location?.latitude
      ? {
          startLatitude: formData.start_location.latitude,
          startLongitude: formData.start_location.longitude,
        }
      : firstRide
        ? { startLatitude: firstRide.startLatitude, startLongitude: firstRide.startLongitude }
        : {};

    const destCoords = formData.destination_location?.latitude
      ? {
          destinationLatitude: formData.destination_location.latitude,
          destinationLongitude: formData.destination_location.longitude,
        }
      : firstRide
        ? { destinationLatitude: firstRide.destinationLatitude, destinationLongitude: firstRide.destinationLongitude }
        : {};

    // Exklusivität hart durchsetzen, egal wie der State zustande kam.
    const isKrankenfahrt = !!formData.is_krankenfahrt;
    const isRechnungsfahrt = !isKrankenfahrt && !!formData.is_rechnungsfahrt;

    return {
      customerId: firstRide?.customerId,
      customerName: formData.customer_name,
      customerPhone: formData.customer_phone,
      start: formData.start,
      destination: formData.destination,
      vehicleType: formData.vehicle_type,
      ...startCoords,
      ...destCoords,
      // Zeitplan-Felder werden ab jetzt immer mitgesendet, damit Änderungen
      // an Uhrzeit oder Wochentagen auf alle zukünftigen Fahrten wirken.
      recurring_weekdays: formData.recurring_weekdays,
      recurring_time: new Date(formData.recurring_time),
      newEndDate: formData.new_end_date ? new Date(formData.new_end_date) : null,
      // Fahrttyp der Serie: Kranken- und Rechnungsfahrt schließen sich aus
      // (rechnungskunden_konzept §2). Beide Blöcke werden immer geschrieben,
      // damit widersprüchliche Altdaten (beide Flags gesetzt) beim Speichern
      // auf allen zukünftigen Fahrten bereinigt werden.
      isRechnungsfahrt: isRechnungsfahrt,
      invoiceCustomerId: (isRechnungsfahrt && formData.invoice_customer_id) ? formData.invoice_customer_id : null,
      invoiceCustomerName: (isRechnungsfahrt && formData.invoice_customer_id)
        ? (invoiceCustomers.find((c) => c.id === formData.invoice_customer_id)?.displayName
           || invoiceCustomers.find((c) => c.id === formData.invoice_customer_id)?.companyName || null)
        : null,
      invoiceBillingStatus: (isRechnungsfahrt && formData.invoice_customer_id) ? 'pending' : null,
      isKrankenfahrt: isKrankenfahrt,
      medicalTransportOrderId: isKrankenfahrt ? (formData.medical_transport_order_id || null) : null,
      // Snapshot aus der gewählten Verordnung. Ist sie nicht geladen, bleibt
      // bei unveränderter Zuordnung der bestehende Snapshot erhalten.
      medicalTransportSnapshot: !isKrankenfahrt
        ? null
        : selectedMedicalOrder
          ? buildMedicalTransportSnapshot(selectedMedicalOrder)
          : (formData.medical_transport_order_id === initialMedical.orderId ? initialMedical.snapshot : null),
    };
  };

  const computePreview = () => {
    if (!currentOrder) return { updateCount: 0, deleteCount: 0, newCount: 0 };

    const now = Date.now();
    const weekdays = formData.recurring_weekdays;
    const newTime = new Date(formData.recurring_time);

    const futureRides = getFutureRides(currentOrder, { statuses: OPEN_RIDE_STATUSES, now });

    let updateCount = 0;
    let deleteCount = 0;
    const occupiedDays = new Set();
    let lastKept = null;

    futureRides.forEach(r => {
      const scheduled = toRideDate(r.scheduledTime);
      const weekday = scheduled.getDay();

      if (!weekdays.includes(weekday)) {
        if (r.status === 'scheduled') {
          deleteCount += 1;
        } else {
          updateCount += 1;
        }
        return;
      }

      updateCount += 1;
      const key = `${scheduled.getFullYear()}-${scheduled.getMonth()}-${scheduled.getDate()}`;
      occupiedDays.add(key);

      const adjusted = new Date(scheduled);
      adjusted.setHours(newTime.getHours(), newTime.getMinutes(), 0, 0);
      if (!lastKept || adjusted > lastKept) lastKept = adjusted;
    });

    let newCount = 0;
    const rangeEnd = formData.new_end_date ? new Date(formData.new_end_date) : (lastKept ? new Date(lastKept) : null);
    if (rangeEnd) {
      rangeEnd.setHours(23, 59, 59, 999);
      const rangeStart = new Date(Math.min(now, lastKept ? lastKept.getTime() : now));
      rangeStart.setHours(0, 0, 0, 0);
      const cursor = new Date(rangeStart);
      while (cursor <= rangeEnd) {
        const key = `${cursor.getFullYear()}-${cursor.getMonth()}-${cursor.getDate()}`;
        if (weekdays.includes(cursor.getDay()) && !occupiedDays.has(key)) {
          const slot = new Date(cursor);
          slot.setHours(newTime.getHours(), newTime.getMinutes(), 0, 0);
          if (slot.getTime() > now) newCount += 1;
        }
        cursor.setDate(cursor.getDate() + 1);
      }
    }

    return { updateCount, deleteCount, newCount };
  };

  const handleOpenConfirm = async (e) => {
    e.preventDefault();
    if (!currentOrder) return;
    // Vorschlagspflicht (nextcab_mobile/docs/general/adressauswahl_konzept.md
    // §3): Getippte Adressen zählen nur als genaue Hausadresse, sonst
    // Vorschlag oder Kartenpunkt.
    const resolvedLocations = {};
    for (const [field, label] of [['start', 'Startadresse'], ['destination', 'Zieladresse']]) {
      const address = (formData[field] || '').trim();
      if (!address || hasCoordinates(formData[`${field}_location`])) continue;
      const precise = await geocodePreciseAddress(address);
      if (!precise) {
        setSubmitError(`${label} ist nicht eindeutig — bitte einen Vorschlag aus der Liste wählen oder auf der Karte festlegen.`);
        return;
      }
      resolvedLocations[`${field}_location`] = { latitude: precise.latitude, longitude: precise.longitude };
    }
    if (Object.keys(resolvedLocations).length > 0) {
      setFormData(prev => ({ ...prev, ...resolvedLocations }));
    }
    if (!formData.recurring_weekdays || formData.recurring_weekdays.length === 0) {
      setSubmitError('Mindestens ein Wochentag muss ausgewählt sein.');
      return;
    }
    if (formData.is_rechnungsfahrt && !formData.invoice_customer_id) {
      setSubmitError('Bitte einen Rechnungskunden auswählen.');
      return;
    }
    // Verordnungspflicht (KONZEPT_KRANKENFAHRT_VERORDNUNGSPFLICHT §3): eine
    // Krankenfahrt ohne Verordnung darf nicht entstehen. Die Schnellanlage
    // einer Platzhalter-Verordnung gibt es nur in der Einzelfahrt (RideForm).
    if (formData.is_krankenfahrt && !formData.medical_transport_order_id) {
      setSubmitError('Bitte eine Verordnung auswählen — Krankenfahrten ohne Verordnung sind nicht zulässig. '
        + 'Neue Verordnung unter „Krankenfahrten" anlegen oder über die Einzelfahrt-Bearbeitung schnellanlegen.');
      return;
    }
    // Sicherheitsnetz analog RideForm validateForm(): getrennte Abrechnungs-
    // wege, eine Krankenfahrt-Serie darf nicht zusätzlich Rechnungsfahrt sein.
    if (formData.is_krankenfahrt && formData.is_rechnungsfahrt) {
      setSubmitError('Kranken- und Rechnungsfahrt schließen sich aus — bitte nur eines wählen.');
      return;
    }
    setSubmitError(null);
    setPreview(computePreview());
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setConfirmOpen(false);
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await dispatch(updateRecurringOrder({
        recurringOrderId,
        updateData: buildUpdateData(),
      }));

      if (result.error) {
        throw new Error(result.error.message || 'Fehler beim Aktualisieren');
      }

      await dispatch(fetchRecurringRides());
      navigate('/fahrten/dauerauftraege');
    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && !currentOrder) {
    return <Loading message="Dauerauftrag wird geladen..." />;
  }

  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden des Dauerauftrags"
        message={error}
        onRetry={() => dispatch(fetchRecurringRides())}
      />
    );
  }

  if (!currentOrder) {
    return (
      <ErrorMessage
        title="Dauerauftrag nicht gefunden"
        message="Der angeforderte Dauerauftrag konnte nicht gefunden werden."
      />
    );
  }

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    
    // Stelle sicher, dass es ein gültiges Date-Objekt ist
    const dateObj = date instanceof Date ? date : new Date(date);
    
    // Prüfe ob das Date-Objekt gültig ist
    if (isNaN(dateObj.getTime())) return '';
    
    return dateObj.toISOString().split('T')[0];
  };

  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <PageHeader
        title="Dauerauftrag bearbeiten"
        subtitle={`Bearbeitung für ${currentOrder.customer_name}`}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrten', path: '/fahrten' },
          { label: 'Daueraufträge', path: '/fahrten/dauerauftraege' },
          { label: 'Bearbeiten' }
        ]}
      />

      <Paper sx={{ p: 3, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/fahrten/dauerauftraege')}
            sx={{ mr: 2 }}
          >
            Zurück zur Übersicht
          </Button>
        </Box>

        <form onSubmit={handleOpenConfirm}>
          <Grid container spacing={3}>
            {/* Kundeninformationen */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom>
                Kundeninformationen
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="customer_name"
                label="Kundenname"
                value={formData.customer_name}
                disabled
                sx={{ 
                  '& .MuiInputBase-root': { 
                    backgroundColor: 'surface.100'
                  } 
                }}
                helperText="Kundendaten können nicht geändert werden"
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="customer_phone"
                label="Telefonnummer"
                value={formData.customer_phone}
                onChange={handleInputChange}
                helperText="Telefonnummer kann aktualisiert werden"
              />
            </Grid>

            {/* Route */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Route
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <PlaceAutocomplete
                fullWidth
                label="Startadresse"
                subDestinationRole="pickup"
                requireLocation
                location={formData.start_location}
                value={formData.start}
                // Getippter Text verwirft die Koordinate der vorigen Adresse;
                // nach einer Auswahl meldet das Element denselben Text noch einmal.
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  start: e.target.value,
                  start_location: e.target.value === prev.start
                    ? prev.start_location
                    : { latitude: null, longitude: null },
                }))}
                onPlaceSelect={(address, location) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    start: address,
                    start_location: location
                  }));
                }}
                required
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <PlaceAutocomplete
                fullWidth
                label="Zieladresse"
                subDestinationRole="destination"
                requireLocation
                location={formData.destination_location}
                value={formData.destination}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  destination: e.target.value,
                  destination_location: e.target.value === prev.destination
                    ? prev.destination_location
                    : { latitude: null, longitude: null },
                }))}
                onPlaceSelect={(address, location) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    destination: address,
                    destination_location: location
                  }));
                }}
                required
              />
            </Grid>

            {/* Fahrzeugtyp */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Fahrzeugtyp</InputLabel>
                <Select
                  name="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={handleInputChange}
                  label="Fahrzeugtyp"
                >
                  {VEHICLE_TYPE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Fahrttyp der Serie: Kranken- ODER Rechnungsfahrt */}
            <Grid size={12}>
              {mixedFlags && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  {mixedFlags.count} von {mixedFlags.total} Fahrten dieser Serie haben einen abweichenden oder
                  widersprüchlichen Fahrttyp (z.&nbsp;B. Kranken- und Rechnungsfahrt gleichzeitig).
                  Beim Speichern wird die hier gewählte Einstellung auf alle zukünftigen Fahrten übernommen
                  und die Serie damit bereinigt.
                </Alert>
              )}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 0.5, sm: 2 }, flexWrap: 'wrap' }}>
                {/* Kranken- und (Firmen-)Rechnungsfahrt schließen sich aus
                    (getrennte Abrechnungswege, rechnungskunden_konzept §2):
                    das Aktivieren der einen deaktiviert die andere. */}
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={formData.is_krankenfahrt}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        is_krankenfahrt: e.target.checked,
                        medical_transport_order_id: e.target.checked ? prev.medical_transport_order_id : '',
                        ...(e.target.checked ? { is_rechnungsfahrt: false, invoice_customer_id: '' } : {})
                      }))}
                    />
                  }
                  label="Krankenfahrt"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={formData.is_rechnungsfahrt}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        is_rechnungsfahrt: e.target.checked,
                        invoice_customer_id: e.target.checked ? prev.invoice_customer_id : '',
                        ...(e.target.checked ? { is_krankenfahrt: false, medical_transport_order_id: '' } : {})
                      }))}
                    />
                  }
                  label="Rechnungsfahrt"
                />
                {formData.is_krankenfahrt && (
                  <Autocomplete
                    options={medicalTransportOrders}
                    loading={medicalTransportOrdersLoading}
                    getOptionLabel={(o) => {
                      if (!o) return '';
                      const remaining = Number(o.maxRides || 0) > 0
                        ? `, Rest: ${Math.max(Number(o.maxRides || 0) - Number(o.usedRides || 0) - Number(o.plannedRides || 0), 0)}`
                        : '';
                      return `${o.patientName || 'Ohne Patient'} - ${o.healthInsuranceName || 'Ohne Krankenkasse'}`
                        + ` (${formatDate(o.validFrom)} bis ${formatDate(o.validUntil)}${remaining})`;
                    }}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    value={selectedMedicalOrder}
                    onChange={(e, val) => setFormData(prev => ({ ...prev, medical_transport_order_id: val ? val.id : '' }))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Verordnungsfall"
                        required
                        error={!!submitError && !formData.medical_transport_order_id}
                        helperText={
                          (formData.medical_transport_order_id && !selectedMedicalOrder && !medicalTransportOrdersLoading)
                            ? 'Zugeordnete Verordnung ist hier nicht sichtbar — sie bleibt beim Speichern erhalten, solange nichts anderes gewählt wird.'
                            : 'Gilt für alle zukünftigen Fahrten der Serie'
                        }
                      />
                    )}
                    sx={{ minWidth: { xs: 0, sm: 320 }, width: { xs: '100%', sm: 'auto' } }}
                  />
                )}
                {formData.is_rechnungsfahrt && (
                  <Autocomplete
                    options={invoiceCustomers}
                    getOptionLabel={(o) => `${o.customerNumber ? o.customerNumber + ' · ' : ''}${o.displayName || o.companyName || ''}`}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={invoiceCustomers.find((c) => c.id === formData.invoice_customer_id) || null}
                    onChange={(e, val) => setFormData(prev => ({ ...prev, invoice_customer_id: val ? val.id : '' }))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Rechnungskunde"
                        required
                        error={!!submitError && !formData.invoice_customer_id}
                        helperText="Firma, die diese Fahrt bezahlt"
                      />
                    )}
                    sx={{ minWidth: { xs: 0, sm: 260 }, width: { xs: '100%', sm: 'auto' } }}
                  />
                )}
              </Box>
            </Grid>

            {/* Zeiten und Wiederholung */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Zeitplan
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="time"
                name="recurring_time"
                label="Uhrzeit"
                value={formatTime(formData.recurring_time)}
                onChange={handleTimeChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={12}>
              <Typography variant="body1" gutterBottom>
                Wochentage
              </Typography>
              <FormGroup row>
                {WEEKDAYS.map((day) => (
                  <FormControlLabel
                    key={day.value}
                    control={
                      <Checkbox
                        checked={formData.recurring_weekdays.includes(day.value)}
                        onChange={() => handleWeekdayChange(day.value)}
                      />
                    }
                    label={day.label}
                  />
                ))}
              </FormGroup>
            </Grid>

            {/* Datumsbereich */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                name="current_end_date"
                label="Aktuelles Enddatum"
                value={formatDateForInput(formData.current_end_date)}
                InputLabelProps={{ shrink: true }}
                disabled
                helperText="Das aktuelle Enddatum des Dauerauftrags"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                name="new_end_date"
                label="Neues Enddatum"
                value={formatDateForInput(formData.new_end_date)}
                onChange={(e) => {
                  const dateValue = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    new_end_date: dateValue ? new Date(dateValue) : null
                  }));
                }}
                InputLabelProps={{ shrink: true }}
                helperText="Verlängern Sie das Enddatum, um neue Fahrten zu erstellen"
              />
            </Grid>

            {/* Fehleranzeige */}
            {submitError && (
              <Grid size={12}>
                <Alert severity="error">
                  {submitError}
                </Alert>
              </Grid>
            )}

            {/* Submit Button */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isSubmitting}
                  size="large"
                >
                  {isSubmitting ? 'Wird gespeichert...' : 'Änderungen speichern'}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => navigate('/fahrten/dauerauftraege')}
                  disabled={isSubmitting}
                  size="large"
                >
                  Abbrechen
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Änderungen am Dauerauftrag bestätigen</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Folgende Änderungen werden auf zukünftige Fahrten angewendet:
            <Box component="ul" sx={{ mt: 1, mb: 1 }}>
              <li>{preview.updateCount} Fahrt(en) werden aktualisiert (Adresse, Fahrzeug, Kontakt, Uhrzeit).</li>
              <li>{preview.deleteCount} Fahrt(en) werden entfernt (entfallene Wochentage).</li>
              <li>{preview.newCount} Fahrt(en) werden neu erstellt.</li>
              <li>
                Fahrttyp: {formData.is_krankenfahrt
                  ? `Krankenfahrt${selectedMedicalOrder ? ` (Verordnung: ${selectedMedicalOrder.patientName || 'ohne Patient'})` : ''}`
                  : formData.is_rechnungsfahrt ? 'Rechnungsfahrt' : 'normale Fahrt'} — wird auf alle
                zukünftigen Fahrten übernommen{mixedFlags ? ' und vereinheitlicht abweichende Fahrten' : ''}.
              </li>
            </Box>
            Bereits akzeptierte oder laufende Fahrten werden nicht angerührt.
            Diese Aktion kann nicht automatisch rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Wird gespeichert...' : 'Änderungen anwenden'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </APIProvider>
  );
};

export default RecurringOrderEditForm;